'use client'

import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { useRouter } from '@/app/navigation/routerProgress';
import { login as serverLogin, logout as serverLogout } from '../actions/auth';
import { clearNotificationSession } from '../notifications/notificationSessionCache';
import { AUTH_UNAUTHORIZED_EVENT, clearAuth } from '@/lib/auth-utils';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  userId: number | null;
  login: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  const invalidateSession = useCallback(() => {
    if (typeof window === 'undefined') return;

    const returnUrl = window.location.pathname + window.location.search;
    if (window.location.pathname !== '/login') {
      window.sessionStorage.setItem('postLoginRedirect', returnUrl);
    }

    try {
      const storedUser = window.localStorage.getItem('authUserData');
      const storedUserId = storedUser ? Number(JSON.parse(storedUser)?.id) : null;
      if (storedUserId != null && Number.isFinite(storedUserId)) {
        clearNotificationSession(window.sessionStorage, storedUserId);
      }
    } catch {
      // Invalid cached user data is removed below with the rest of the session.
    }

    clearAuth();
    setIsAuthenticated(false);
    setToken(null);
    setUserId(null);
    setIsLoading(false);
    router.replace('/login');
  }, [router]);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        if (typeof window !== 'undefined') {
          const authToken = localStorage.getItem('authToken');
          const authUserData = localStorage.getItem('authUserData');
          if (!authToken || !authUserData) {
            if (!cancelled) {
              setIsAuthenticated(false);
              setToken(null);
              setUserId(null);
            }
            return;
          }

          let id: number | null = null;
          try {
            const parsedId = JSON.parse(authUserData)?.id;
            id = parsedId != null && Number.isFinite(Number(parsedId)) ? Number(parsedId) : null;
          } catch {
            invalidateSession();
            return;
          }

          const response = await fetch(`${API_BASE_URL}/api/user`, {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            cache: 'no-store',
          });

          if (response.status === 401) {
            invalidateSession();
            return;
          }

          if (!cancelled) {
            // A temporary network/server/authorization failure must not destroy a valid
            // local session. Only an explicit 401 means the token is no longer usable.
            setIsAuthenticated(true);
            setToken(authToken);
            setUserId(id);
          }
        }
      } catch {
        if (!cancelled) {
          const authToken = localStorage.getItem('authToken');
          const authUserData = localStorage.getItem('authUserData');
          setIsAuthenticated(!!authToken && !!authUserData);
          setToken(authToken);
          try {
            const parsedId = authUserData ? Number(JSON.parse(authUserData)?.id) : null;
            setUserId(parsedId != null && Number.isFinite(parsedId) ? parsedId : null);
          } catch {
            setUserId(null);
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void checkAuth();

    if (typeof window !== 'undefined') {
      const handleStorage = (event: StorageEvent) => {
        if (event.key === null || ['authToken', 'authUserData', 'user'].includes(event.key)) {
          void checkAuth();
        }
      };
      window.addEventListener('storage', handleStorage);
      window.addEventListener(AUTH_UNAUTHORIZED_EVENT, invalidateSession);
      return () => {
        cancelled = true;
        window.removeEventListener('storage', handleStorage);
        window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, invalidateSession);
      };
    }
  }, [invalidateSession]);

  const login = async (email: string, password: string) => {
    try {
      const response = await serverLogin(email, password);
      if (response?.token) {
        // Safely set localStorage items
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('authToken', response.token);
            // Keep the organization name with the user, so the header can show it
            localStorage.setItem('authUserData', JSON.stringify({
              ...response.user,
              organization_name: response.organizationName ?? null,
            }));

          } catch (error) {
            console.error('Error setting auth data in localStorage:', error);
          }
        }
        setIsAuthenticated(true);
        setToken(response.token);
        setUserId(response.user?.id != null ? Number(response.user.id) : null);
        return response;
      }
      return null;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await serverLogout();
      // Safely remove localStorage items
      if (typeof window !== 'undefined') {
        try {
          if (userId) clearNotificationSession(window.sessionStorage, userId);
          clearAuth();
        } catch (error) {
          console.error('Error removing auth data from localStorage:', error);
        }
      }
      setIsAuthenticated(false);
      setToken(null);
      setUserId(null);
      router.push('/login');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, token, userId, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
